import dayjs from 'dayjs';
import * as _ from 'lodash';
import * as React from 'react';
import { FormattedDate, FormattedNumber } from 'react-intl';
import { Column, UseTableCellProps, UseTableRowProps } from 'react-table';

import { PredefinedFiltersEnum, SnackBarVariants } from '@app/app.enum';
import { withAxiosCancellation } from '@app/components/core/axios-cancel.component';
import withSnackBar from '@app/components/core/message.component';
import ReportBalanceHistoryComponent from '@app/components/report/balance-history/balance-history.component';
import CustomDatePicker from '@app/components/shared/custom-date-picker.component';
import Icon from '@app/components/shared/icon.component';
import PredefinedFilters from '@app/components/shared/predefined-filters.component';
import Table from '@app/components/shared/table.component';
import * as constants from '@app/constants';
import * as models from '@app/models';
import { initialReportDailyFinanceComponentState } from '@app/models/report.models';
import Injector from '@app/services';
import { IntlService } from '@app/services/intl.service';
import { ReportService } from '@app/services/report.service';
import { SharedService } from '@app/services/shared.service';
import Axios from 'axios';

import { Box, Button, Card, CardContent, Grid, Hidden, IconButton, Tooltip } from '@material-ui/core';

const intlService: IntlService = Injector.get(IntlService);
const reportService: ReportService = Injector.get(ReportService);
const sharedService: SharedService = Injector.get(SharedService);
const pageSize = 20;

const RowContentComponent = (
	props: UseTableRowProps<models.IDailyMonthlyFinance>,
	startDate?: Date,
	endDate?: Date
) => {
	return (
		<ReportBalanceHistoryComponent
			contained={true}
			rows={props.original.accountEntries}
			startDate={startDate}
			endDate={endDate}
		/>
	);
};
class ReportDailyFinanceComponent extends React.Component<
	models.IDailyFinanceComponentProps,
	models.IReportDailyFinanceComponentState
> {
	constructor(props: models.IDailyFinanceComponentProps) {
		super(props);
		this.state = initialReportDailyFinanceComponentState(props.rows, !props.contained);
	}

	public componentDidMount() {
		if (this.props.contained) {
			return;
		}
		const startDate = this.props.startDate ? this.props.startDate : this.state.startDate;
		const endDate = this.props.endDate ? this.props.endDate : this.state.endDate;
		this.loadData(this.state.currentPage, startDate, endDate);
	}

	public componentDidUpdate(
		prevProps: models.IDailyFinanceComponentProps,
		prevState: models.IReportDailyFinanceComponentState
	) {
		if (this.shouldFetchData(prevState)) {
			this.loadData(this.state.currentPage, this.state.startDate, this.state.endDate);
		}
	}

	public render() {
		const filterRow = this.props.contained ? null : this.getFilterRow();
		return (
			<React.Fragment>
				<Grid container={true} spacing={2}>
					<Grid item={true} lg={12} md={12} sm={12} xs={12}>
						<Card>
							<CardContent>
								{filterRow}
								<Hidden only={['md', 'sm', 'xs']}>
									<Grid container={true}>
										<Table<models.IDailyMonthlyFinance>
											columns={this.getGridColumns()}
											data={this.state.results}
											dataLoading={this.state.dataLoading}
											rowContent={(props: UseTableRowProps<models.IDailyMonthlyFinance>) =>
												RowContentComponent(props, this.state.startDate, this.state.endDate)
											}
											onPageChange={(e: object, page: number) => this.setState({ currentPage: page })}
											currentPage={this.state.currentPage}
											totalPages={this.state.totalPages}
											pageSize={pageSize}
											totalRecords={this.state.totalResults}
											pageJump={this.state.pageJump}
											getCurrentPage={() => this.state.currentPage}
											getPageJump={() => this.state.pageJump}
											setCurrentPage={(page: number) => {
												this.setState({
													currentPage: page,
												});
											}}
											setPageJump={(page: number) => {
												this.setState({
													pageJump: page,
												});
											}}
										/>
									</Grid>
								</Hidden>
							</CardContent>
						</Card>
					</Grid>
				</Grid>
			</React.Fragment>
		);
	}

	private getFilterRow() {
		return (
			<Grid container={true} spacing={2}>
				<Grid item={true} lg={8} md={12} sm={12} xs={12}>
					<Grid container={true} justifyContent='flex-start' spacing={2}>
						<Grid item={true} lg={4} md={4} sm={12} xs={12}>
							<PredefinedFilters
								startDate={this.state.startDate}
								endDate={this.state.endDate}
								onDateChange={this.callAfterCancellingActiveRequests(this.loadDataForPredefinedFilters)}
							/>
						</Grid>
						<Grid item={true} lg={4} md={4} sm={12} xs={12}>
							<CustomDatePicker
								label={intlService.GetFormattedMessage('app.balance-history.start-date-label')}
								maxDate={this.state.endDate}
								disabled={this.state.disableDatePickerFilter}
								value={this.state.startDate}
								onChange={(date: dayjs.Dayjs) => {
									const composedFunc = this.callAfterCancellingActiveRequests(() =>
										this.setState({
											results: [],
											startDate: date.toDate(),
											currentPage: 1,
										})
									);
									composedFunc();
								}}
							/>
						</Grid>
						<Grid item={true} lg={4} md={4} sm={12} xs={12}>
							<CustomDatePicker
								label={intlService.GetFormattedMessage('app.balance-history.end-date-label')}
								minDate={this.state.startDate}
								disabled={this.state.disableDatePickerFilter}
								value={this.state.endDate}
								onChange={(date: dayjs.Dayjs) => {
									const composedFunc = this.callAfterCancellingActiveRequests(() =>
										this.setState({
											results: [],
											endDate: date.toDate(),
											currentPage: 1,
										})
									);
									composedFunc();
								}}
							/>
						</Grid>
					</Grid>
				</Grid>
				<Grid item={true} lg={4} md={12} sm={12} xs={12}>
					<Hidden only={['sm', 'xs', 'md']}>
						<Box height='100%' alignItems='center' display='flex'>
							<Tooltip title={intlService.GetFormattedMessage('app.common.export')}>
								<IconButton onClick={() => this.exportReport()}>
									<Icon name='tray-arrow-down' />
								</IconButton>
							</Tooltip>
						</Box>
					</Hidden>
					<Hidden only={['xl', 'lg']}>
						<Button variant='outlined' color='primary' onClick={() => this.exportReport()}>
							<Icon name='tray-arrow-down' />
							<span className='pl-1'>{intlService.GetFormattedMessage('app.common.export')}</span>
						</Button>
					</Hidden>
				</Grid>
			</Grid>
		);
	}

	private getGridColumns = (): Array<Column<models.IDailyMonthlyFinance>> => {
		return [
			{
				// Make an expander cell
				Header: '', // No header
				id: 'expander', // It needs an ID
				Cell: ({ row }: any) => (
					// Use Cell to render an expander for each row.
					// We can use the getToggleRowExpandedProps prop-getter
					// to build the expander.
					<span {...row.getToggleRowExpandedProps()}>
						{row.isExpanded ? (
							<Icon name='caret-down' fontSize='small' />
						) : (
							<Icon name='caret-right' fontSize='small' />
						)}
					</span>
				),
				disableSortBy: true,
			},
			{
				accessor: 'date',
				Header: intlService.GetFormattedMessage('app.daily-finance.date-table-header'),
				Cell: (tableData: UseTableCellProps<models.IDailyMonthlyFinance>) => (
					<FormattedDate {...constants.dateFormatOptions} value={tableData.value} />
				),
				disableSortBy: true,
			},
			{
				accessor: 'beginningBalance',
				Header: intlService.GetFormattedMessage('app.daily-finance.beginning-balance-table-header'),
				Cell: (tableData: UseTableCellProps<models.IDailyMonthlyFinance>) => (
					<>
						$
						<FormattedNumber value={tableData.value} maximumFractionDigits={2} minimumFractionDigits={2} />
					</>
				),
				disableSortBy: true,
			},
			{
				accessor: 'creditCardCreditAmount',
				Header: intlService.GetFormattedMessage('app.daily-finance.funds-added-table-header'),
				Cell: (tableData: UseTableCellProps<models.IDailyMonthlyFinance>) => (
					<>
						$
						<FormattedNumber value={tableData.value} maximumFractionDigits={2} minimumFractionDigits={2} />
					</>
				),
				disableSortBy: true,
			},
			{
				accessor: 'labelDebitAmount',
				Header: intlService.GetFormattedMessage('app.daily-finance.funds-printed-table-header'),
				Cell: (tableData: UseTableCellProps<models.IDailyMonthlyFinance>) => (
					<>
						$
						<FormattedNumber value={tableData.value} maximumFractionDigits={2} minimumFractionDigits={2} />
					</>
				),
				disableSortBy: true,
			},
			{
				accessor: 'creditCardDebitAmount',
				Header: intlService.GetFormattedMessage('app.daily-finance.funds-refunded-table-header'),
				Cell: (tableData: UseTableCellProps<models.IDailyMonthlyFinance>) => (
					<>
						$
						<FormattedNumber value={tableData.value} maximumFractionDigits={2} minimumFractionDigits={2} />
					</>
				),
				disableSortBy: true,
			},
			{
				accessor: 'totalBalance',
				Header: intlService.GetFormattedMessage('app.daily-finance.ending-balance-table-header'),
				Cell: (tableData: UseTableCellProps<models.IDailyMonthlyFinance>) => (
					<>
						$
						<FormattedNumber value={tableData.value} maximumFractionDigits={2} minimumFractionDigits={2} />
					</>
				),
				disableSortBy: true,
			},
		];
	};

	private loadData = async (requestedPage: number, startDate: Date, endDate: Date) => {
		try {
			let pageToken = sharedService.generatePaginationToken(requestedPage - 2);

			if (requestedPage === 0) {
				pageToken = undefined;
			}
			let accountStatementByDay: models.IPagedResult<models.IDailyMonthlyFinance> = {
				results: [],
				nextPage: '',
				previousPage: '',
				pageSize: 0,
				totalResults: 0,
				totalPages: 0,
			};
			this.setState({
				dataLoading: true,
				results: [],
			});
			if (!this.props.contained) {
				accountStatementByDay = await reportService.getDailyFinance(
					pageToken,
					startDate,
					endDate,
					this.props.axiosCancellationToken
				);
			}
			this.setState({
				...accountStatementByDay,
				dataLoading: false,
			});
		} catch (error) {
			if (!Axios.isCancel(error)) {
				this.props.openSnackBar(sharedService.parseError(error.data), SnackBarVariants.Error);
			}
		}
	};

	/**
	 * Export report
	 */
	private exportReport = async () => {
		const res = await reportService.exportDailyFinanceReport(
			this.state.startDate,
			this.state.endDate,
			this.props.axiosCancellationToken
		);
		const fileName = sharedService.parseContentDisposition(
			res.request.getResponseHeader(constants.contentDispositionHeader)
		);
		sharedService.downloadFile(res.data, fileName);
	};

	private loadDataForPredefinedFilters = (startDate: Date, endDate: Date, filterValue: PredefinedFiltersEnum) => {
		this.setState({
			results: [],
			startDate,
			endDate,
			disableDatePickerFilter: filterValue !== PredefinedFiltersEnum.Custom,
		});
	};

	private shouldFetchData = (prevState: models.IReportDailyFinanceComponentState): boolean => {
		return (
			(prevState.startDate !== this.state.startDate ||
				prevState.endDate !== this.state.endDate ||
				prevState.currentPage !== this.state.currentPage) &&
			!this.props.isRequestCancelled
		);
	};

	private callAfterCancellingActiveRequests = (callback: (...args: any) => void) => {
		return async (...args: any) => {
			await this.props.cancelActiveRequests();
			callback(...args);
		};
	};
}

export default withAxiosCancellation(withSnackBar(ReportDailyFinanceComponent));



// WEBPACK FOOTER //
// ./src/components/report/daily-finance/daily-finance.component.tsx